
import { Button, Table, Popconfirm } from "@/frontend/components/common";
import { roleService } from "@/frontend/services";
import type { FlatPaginationRequestDTO, RoleResponseDTO } from "@/shared/dto";
import { formatDate } from "@/shared/utils";
import { FaPlus, FaEdit, FaTrash } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";
import { usePagination, useDelete } from "@/frontend/hooks";
import { getPlatformAccessInfo, getRoleStateInfo } from "@/frontend/utils";
import { useAuthStore } from "@/frontend/stores";
import { RolesGuardEnum } from "@/core/enums";
import { useState } from "react";
import { DatePicker, Select } from "antd";

const { RangePicker } = DatePicker;

const initialPagination: TablePaginationConfig = {
  pageSizeOptions: ["7","10", "20", "50", "100"],
  showSizeChanger: true,
  showTotal: (total) => `Total: ${total} registros`,
};

const initialParams: FlatPaginationRequestDTO = { page: 1, pageSize: 7 };
export function RolesListPage() {
  const navigate = useNavigate();
  const { data, loading, pagination, loadData } = usePagination<RoleResponseDTO>({
    fetchFn: roleService.getPaginate,
    initialParams: initialParams,
    initialPagination: initialPagination,
  });

  const [filterStatus, setFilterStatus] = useState<string | undefined>();
  const [filterPlatformAccess, setFilterPlatformAccess] = useState<string | undefined>();
  const [dateRange, setDateRange] = useState<[string, string] | null>(null);

  function applyFilters(status?: string, platformAccess?: string, range?: [string, string] | null) {
    loadData({
      page: 1,
      pageSize: 7,
      status,
      platformAccess,
      createdFrom: range?.[0],
      createdTo: range?.[1],
    });
  }

  const { hasPermission } = useAuthStore();

  const { handleDelete, showConfirm, confirmTitle, confirmDescription } = useDelete({
    deleteFn: roleService.delete,
    loadData,
    successMessage: "Rol eliminado exitosamente",
    errorMessage: "Error al eliminar rol",
    showConfirm: true,
    confirmTitle: "¿Eliminar rol?",
    confirmDescription: "Esta acción eliminará el rol permanentemente. ¿Estás seguro?",
  });



  const columns: ColumnsType<RoleResponseDTO> = [
    {
      title: "Nombre",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Guard Slug",
      dataIndex: "guardSlug",
      key: "guardSlug",
    },
    {
      title: "Descripción",
      dataIndex: "description",
      key: "description",
      render: (description: string | null) => description || "-",
    },
    {
      title: "Estado",
      dataIndex: "status",
      key: "status",
      render: (status: string) => {
        const stateInfo = getRoleStateInfo(status);
        return (
          <span className={stateInfo.color}>
            {stateInfo.label}
          </span>
        );
      },
    },
    {
      title: "Acceso a Plataforma",
      dataIndex: "platformAccess",
      key: "platformAccess",
      render: (access: string) => {
        const accessInfo = getPlatformAccessInfo(access);
        return (
          <span className={accessInfo.color}>
            {accessInfo.label}
          </span>
        );
      },
    },
    {
      title: "Fecha de Creación",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date: Date) => formatDate(date),
    },
    {
      title: "Acciones",
      key: "actions",
      width: 150,
      render: (_: any, record: RoleResponseDTO) => (
        <div className="flex gap-2">
          <Button
            type="link"
            disabled={!hasPermission(RolesGuardEnum.ROLES_UPDATE)}
            icon={<FaEdit />}
            onClick={() => navigate(`/roles/edit/${record.id}`)}
          >
            Editar
          </Button>
          {showConfirm ? (
            <Popconfirm
              title={confirmTitle}
              description={confirmDescription}
              onConfirm={() => handleDelete(record.id)}
              okText="Sí, eliminar"
              cancelText="Cancelar"
              okButtonProps={{ danger: true }}
            >
              <Button
                type="link"
                danger
                disabled={!hasPermission(RolesGuardEnum.ROLES_DELETE)}
                icon={<FaTrash />}
              >
                Eliminar
              </Button>
            </Popconfirm>
          ) : (
            <Button
              type="link"
              danger
              disabled={!hasPermission(RolesGuardEnum.ROLES_DELETE)}
              icon={<FaTrash />}
              onClick={() => handleDelete(record.id)}
            >
              Eliminar
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="bg-white rounded-lg p-6 shadow-sm">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Gestión de Roles</h2>
          <p className="text-gray-600 mt-1">
            Administra los roles de la plataforma de torneos
          </p>
        </div>
        <Button
          type="primary"
          icon={<FaPlus />}
          onClick={() => navigate("/roles/create")}
        >
          Crear Rol
        </Button>
      </div>

      <div className="flex gap-3 mb-4">
        <Select
          placeholder="Estado"
          allowClear
          value={filterStatus}
          onChange={(val) => { setFilterStatus(val); applyFilters(val, filterPlatformAccess, dateRange); }}
          options={[
            { label: "Activo", value: "active" },
            { label: "Inactivo", value: "inactive" },
          ]}
          style={{ width: 150 }}
        />

        <Select
          placeholder="Acceso a plataforma"
          allowClear
          value={filterPlatformAccess}
          onChange={(val) => { setFilterPlatformAccess(val); applyFilters(filterStatus, val, dateRange); }}
          options={[
            { label: "Web", value: "web" },
            { label: "Mobile", value: "mobile" },
            { label: "Ambos", value: "both" },
          ]}
          style={{ width: 180 }}
        />

        <RangePicker
          placeholder={["Fecha inicio", "Fecha fin"]}
          onChange={(_, dateStrings) => {
            const range = dateStrings && dateStrings[0] && dateStrings[1]
              ? [dateStrings[0], dateStrings[1]] as [string, string]
              : null;
            setDateRange(range);
            applyFilters(filterStatus, filterPlatformAccess, range);
          }}
        />
      </div>

      <Table
        columns={columns}
        dataSource={data}
        loading={loading}
        rowKey="id"
        pagination={pagination}
      />
    </div>
  );
}
